import { BlockPoint, MetadataTableName } from '@bechtel/sr-types';
import { SortChangedEvent } from 'ag-grid-community';
import { filter, includes, isEqual, orderBy } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import { ClientGrid } from '~/components/organisms/grid';

interface PointsProps {
  onBlockPointsChanged: (equipmentBlockPoints: BlockPoint[]) => void;
  canEdit: boolean;
  blockPointList?: BlockPoint[] | null;
  initialBlockPoints?: BlockPoint[] | null;
}

export const Points: FC<PointsProps> = ({ blockPointList, onBlockPointsChanged, initialBlockPoints, canEdit }) => {
  const [blockPoints, setBlockPoints] = useState<BlockPoint[]>(blockPointList || []);

  useEffect(() => {
    setBlockPoints(orderBy(blockPointList, (blockPoint: BlockPoint) => blockPoint.sequenceNumber) || []);
  }, [blockPointList]);

  const onPointEdited = useCallback(
    (blockPoint: BlockPoint) => {
      const index = blockPoints?.findIndex((item) => item?.id === blockPoint?.id);
      if (index === -1) {
        // Id is required as tag are editable, after saving the grid data needs to be refreshed with the correct ID generated from the backend

        const newBlockPoint = {
          ...blockPoint,
          id: blockPoint.id ? blockPoint.id : crypto.randomUUID(), // Id is required as tag are editable, after saving the grid data needs to be refreshed with the correct ID generated from the backend
          sequenceNumber: blockPoints.length + 1,
        };
        onBlockPointsChanged([...blockPoints, newBlockPoint]);
      } else {
        const updatedBlockPoints = [...blockPoints];
        updatedBlockPoints[index] = blockPoint;
        onBlockPointsChanged(updatedBlockPoints);
      }
    },
    [blockPoints, onBlockPointsChanged]
  );

  const onPointDeleted = useCallback(
    (deletedBlockPoints: string[]) => {
      const updatedPoints = filter(blockPoints, (item) => !includes(deletedBlockPoints, item.id));
      onBlockPointsChanged([...updatedPoints]);
    },
    [blockPoints, onBlockPointsChanged]
  );

  const onPointRowDragged = useCallback(
    (fromIndex, toIndex) => {
      const arrangedBlockPoints = blockPoints.slice();
      const draggedBlock = arrangedBlockPoints[fromIndex];
      const clonedDraggedBlock = { ...draggedBlock, sequenceNumber: toIndex + 1 };
      arrangedBlockPoints.splice(fromIndex, 1);
      arrangedBlockPoints.splice(toIndex, 0, clonedDraggedBlock);
      const arrangedBlockPointsAsPerGangSequence = arrangedBlockPoints.map((blockPoint: BlockPoint, index: number) => {
        return { ...blockPoint, gangSequence: index + 1 };
      });
      setBlockPoints(arrangedBlockPointsAsPerGangSequence);
      onBlockPointsChanged(arrangedBlockPointsAsPerGangSequence);
      return arrangedBlockPointsAsPerGangSequence;
    },
    [blockPoints, onBlockPointsChanged]
  );

  const onSortChanged = (e: SortChangedEvent) => {
    const sortedBlockPoints: BlockPoint[] = [];
    e.api.forEachNodeAfterFilterAndSort((node, index: number) => {
      if (node.data?.id) sortedBlockPoints.push({ ...node.data, sequenceNumber: index + 1 });
    });
    if (!isEqual(blockPoints, sortedBlockPoints)) onBlockPointsChanged(sortedBlockPoints);
  };

  return (
    <ClientGrid
      getRowId={(data) => data?.id}
      originalRowData={initialBlockPoints}
      name={MetadataTableName.EQUIPMENT_BLOCK_POINT}
      rowSelection="single"
      hideLayoutHeader={true}
      canCreateLayout={false}
      systemLayoutName={'equipment.equipmentBlockPoint'}
      rowData={blockPoints?.length > 0 ? blockPoints : []}
      onRowEdited={onPointEdited}
      onRowsDeleted={onPointDeleted}
      rowDraggableColumn="tag"
      onRowDragged={onPointRowDragged}
      skipPermission={canEdit}
      onSortChange={onSortChanged}
      headerHeight={48}
    ></ClientGrid>
  );
};
